import * as React from "react";
import type { AdChannel } from "./types";
import { logTrackingDebug } from "../debug/debugLog";
import { sha256Hex } from "./hash";

type Fbq = (...args: unknown[]) => void;

declare global {
  interface Window {
    fbq?: Fbq;
  }
}

function fbq(...args: unknown[]): void {
  if (typeof window === "undefined") {
    return;
  }
  const [command, nameOrID, params] = args;
  logTrackingDebug(
    "meta",
    command === "init" ? "init (advanced matching)" : `${command} ${nameOrID}`,
    // Keys only for init: the values are the user's email and hashed ID.
    command === "init" ? Object.keys((params as object) ?? {}) : params,
  );
  if (!window.fbq) {
    logTrackingDebug("meta", "fbq missing: pixel script not on the page");
    return;
  }
  window.fbq(...args);
}

/**
 * Meta (Facebook) pixel. The <head> snippet loads fbevents.js, inits the pixel
 * and sends PageView once per full page load (same as the
 * gatsby-plugin-facebook-pixel it replaces).
 */
export function createMetaChannel(pixelID: string): AdChannel {
  return {
    name: "meta",

    headComponents: () => [
      <script
        key="meta-pixel"
        dangerouslySetInnerHTML={{
          __html: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init',${JSON.stringify(pixelID)});fbq('track','PageView');`,
        }}
      />,
    ],

    identify: ({ email, userID }) => {
      // Re-init with advanced matching so later events carry `em`. Sync, so the
      // Lead event fired right after identify already has it.
      fbq("init", pixelID, { em: email });
      if (!userID) return;
      // external_id must equal the server-side Purchase's (lovelica-triggers
      // sends sha256(userID)), so hash it ourselves; Meta leaves hashed values as-is.
      sha256Hex(userID.trim())
        .then((externalID) => fbq("init", pixelID, { em: email, external_id: externalID }))
        .catch(() => {
          // No WebCrypto (insecure context): keep email-only matching.
        });
    },

    track: (e, eventId) => {
      const options = { eventID: eventId };
      switch (e.name) {
        case "page-view":
          // PageView itself is sent once per full load by the <head> snippet.
          fbq("track", "ViewContent", {}, options);
          return;
        case "lead-created":
          fbq("track", "Lead", {}, options);
          return;
        case "plan-selected":
          fbq("track", "AddToCart", pick(e.properties), options);
          return;
        case "page_view_checkout":
          fbq("track", "InitiateCheckout", pick(e.properties), options);
          return;
        case "add-payment-info":
          fbq("track", "AddPaymentInfo", pick(e.properties), options);
          return;
      }
    },
  };
}

function pick(p: { value: number; currency: string; productID: string }) {
  return { value: p.value, currency: p.currency, productID: p.productID };
}
